import { useState, useEffect, useRef } from 'react';

/**
 * Security Enhancements Hook
 *
 * Provides client-side security features:
 * - Form timing tracking (start → submit time)
 * - Browser fingerprinting (detect headless browsers)
 * - Honeypot field management
 */

interface SecurityData {
  formStartTime: number;
  formSubmitTime: number;
  browserFeatures: {
    webdriver: boolean;
    plugins: boolean;
    pluginsLength: number;
    languages: string[];
    platform: string;
    userAgent: string;
    screenResolution: string;
    timezone: string;
  };
  honeypotFields: {
    website: string;
    company_url: string;
    phone2: string;
    address: string;
  };
}

export const useSecurityEnhancements = () => {
  const formStartTimeRef = useRef<number>(Date.now());
  const [honeypotFields, setHoneypotFields] = useState({
    website: '',
    company_url: '',
    phone2: '',
    address: '',
  });

  // Initialize form start time once
  useEffect(() => {
    formStartTimeRef.current = Date.now();
  }, []);

  /**
   * Get browser fingerprinting data
   */
  const getBrowserFeatures = () => {
    try {
      const features = {
        webdriver: !!(navigator as any).webdriver,
        plugins: navigator.plugins && navigator.plugins.length > 0,
        pluginsLength: navigator.plugins ? navigator.plugins.length : 0,
        languages: navigator.languages ? Array.from(navigator.languages) : [navigator.language],
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      return features;
    } catch (error) {
      console.error('Error collecting browser features:', error);
      return {
        webdriver: false,
        plugins: false,
        pluginsLength: 0,
        languages: [],
        platform: '',
        userAgent: '',
        screenResolution: '',
        timezone: '',
      };
    }
  };

  /**
   * Update honeypot field value
   */
  const updateHoneypotField = (field: string, value: string) => {
    setHoneypotFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Get complete security data for form submission
   */
  const getSecurityData = (): SecurityData => {
    return {
      formStartTime: formStartTimeRef.current,
      formSubmitTime: Date.now(),
      browserFeatures: getBrowserFeatures(),
      honeypotFields,
    };
  };

  /**
   * Check if submission timing is suspicious (too fast)
   */
  const checkTimingSuspicious = (): { suspicious: boolean; timeDiff: number } => {
    const timeDiff = (Date.now() - formStartTimeRef.current) / 1000; // seconds
    const suspicious = timeDiff < 5; // Less than 5 seconds is suspicious

    return { suspicious, timeDiff };
  };

  /**
   * Check if any honeypot fields were filled (bot behavior)
   */
  const checkHoneypotFilled = (): { filled: boolean; filledFields: string[] } => {
    const filledFields = Object.entries(honeypotFields)
      .filter(([_, value]) => value !== '')
      .map(([field, _]) => field);

    return {
      filled: filledFields.length > 0,
      filledFields,
    };
  };

  /**
   * Perform client-side security checks before submission
   */
  const performSecurityChecks = (): {
    passed: boolean;
    issues: string[];
  } => {
    const issues: string[] = [];

    // Check timing
    const { suspicious, timeDiff } = checkTimingSuspicious();
    if (suspicious) {
      issues.push(`Form submitted too quickly (${timeDiff.toFixed(1)}s)`);
    }

    // Check honeypot - DISABLED: causing false positives with browser autofill
    // const { filled, filledFields } = checkHoneypotFilled();
    // if (filled) {
    //   issues.push(`Honeypot fields filled: ${filledFields.join(', ')}`);
    // }

    // Check for webdriver
    const features = getBrowserFeatures();
    if (features.webdriver) {
      issues.push('Automated browser detected');
    }

    return {
      passed: issues.length === 0,
      issues,
    };
  };

  return {
    honeypotFields,
    updateHoneypotField,
    getSecurityData,
    performSecurityChecks,
    checkTimingSuspicious,
    checkHoneypotFilled,
    getBrowserFeatures,
  };
};

export default useSecurityEnhancements;
